import { Injectable } from '@angular/core'
import { ProgressService } from '@mmx/components'
import { flatten, uniq } from 'lodash'
import { Observable, of, Subject } from 'rxjs'
import {
  combineLatestWith,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators'

import { AppointmentModel } from '../model/appointment'
import { Clinic } from '../model/clinic'
import { Facility } from '../model/facility'
import { IntakeModel } from '../model/intake'
import { Patient } from '../model/patient'
import { ProductModel } from '../model/product'
import { ParamsService } from '../services/params.service'
import { ApiService } from './api.service'
import { ClinicService } from './clinic.service'
import { InsuranceProviderLookupsService } from './insurance-provider-lookups.service'
import { PatientDataService } from './patient-data.service'

const log = (message: string) => {
  // eslint-disable-next-line no-console
  console.debug(`[AppointmentDataService]: ${message}`)
}

enum PROGRESS_KEY {
  APPOINTMENT = 'appointment',
  INTAKE = 'intake',
}

@Injectable({
  providedIn: 'root',
})
export class AppointmentDataService {
  constructor(
    private progressService: ProgressService<PROGRESS_KEY>,
    private patientDataService: PatientDataService,
    private paramsService: ParamsService,
    private clinicService: ClinicService,
    private restService: ApiService,
    private insuranceProviderLookupsService: InsuranceProviderLookupsService,
  ) {}

  private clinic$: Observable<Clinic> = this.clinicService.clinic$

  patient$: Observable<Patient> = this.patientDataService.patient$

  appointmentUpdated$ = new Subject<void>()

  /**
   * Expose the progress monitor
   */
  progress$ = this.progressService.progress$.pipe(
    combineLatestWith(this.patientDataService.progress$),
    map(
      ([appointmentDataProgress, patientDataProgress]) =>
        appointmentDataProgress || patientDataProgress,
    ),
  )

  appointmentId$ = this.paramsService.appointmentId$.pipe(
    map(([, appointmentId]) => appointmentId),
    filter((appointmentId) => Boolean(appointmentId)),
    distinctUntilChanged(),
    tap((appointmentId) => log(`appointmentId = ${appointmentId}`)),
    shareReplay(1),
  )

  appointment$: Observable<AppointmentModel | null> = this.appointmentId$.pipe(
    combineLatestWith(
      this.appointmentUpdated$.pipe(startWith(null)),
      this.patientDataService.insurancesChanged$.pipe(startWith(null)),
    ),
    tap(() => this.progressService.start(PROGRESS_KEY.APPOINTMENT)),
    switchMap(([appointmentId]) =>
      this.restService
        .getAppointment(appointmentId)
        .pipe(
          finalize(() => this.progressService.stop(PROGRESS_KEY.APPOINTMENT)),
        ),
    ),
    filter(Boolean),
    shareReplay(1),
  )

  facility$: Observable<Facility | undefined> = this.appointment$.pipe(
    map((appointment) => appointment.location),
  )

  products$: Observable<ProductModel[]> = this.appointment$.pipe(
    map((appointment) => appointment.products ?? []),
  )

  serviceCodes$: Observable<string[]> = this.products$.pipe(
    map((products) =>
      uniq(flatten(products.map((product) => product.serviceCodes ?? []))),
    ),
  )

  intakeId$: Observable<string | null> = this.appointment$.pipe(
    combineLatestWith(this.clinic$),
    filter(([, clinic]) => clinic.modules.intake),
    map(([appointment]) => appointment.intakeId ?? null),
    startWith(null),
    distinctUntilChanged(),
    tap((intakeId) => log(`intakeId = ${intakeId}`)),
    shareReplay(1),
  )

  intake$: Observable<IntakeModel | null> = this.intakeId$.pipe(
    tap(() => this.progressService.start(PROGRESS_KEY.INTAKE)),
    switchMap((intakeId) =>
      (intakeId ? this.restService.getIntake(intakeId) : of(null)).pipe(
        finalize(() => this.progressService.stop(PROGRESS_KEY.INTAKE)),
      ),
    ),
    shareReplay(1),
  )
}
